import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useRoute } from "wouter";
import { Layout } from "@/components/layout/Layout";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Calendar } from "@/components/ui/calendar";
import { addDays, format, isBefore, isAfter, isSameDay, differenceInCalendarDays, eachDayOfInterval, isWithinInterval } from "date-fns";
import { ChevronLeft, MapPin, Loader2, Check, X, AlertCircle, Bed, ShowerHead, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/api";
import PropertyImageGallery from "@/components/PropertyImageGallery";
import { CalendarDisplay } from "./calendar-display";
import { Helmet } from "react-helmet";
import { formatCurrency } from "@/lib/utils";
import GuestAuthForm from "@/components/auth/GuestAuthForm";
import { DateRange } from "react-day-picker";
import { useGoogleOneTap } from "@/hooks/useGoogleOneTap";
import BookingForm from "@/components/checkout/BookingForm";
import { cn } from "@/lib/utils";

interface Property {
  id: number;
  title: string;
  description: string;
  area: string;
  address: string;
  basePrice?: number;
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  features: string[] | null;
  images?: any[];
  primaryImage?: string;
  lat?: number | null;
  lng?: number | null;
  locationTitle?: string | null;
  locationDescription?: string | null;
  rating?: string | null;
  reviewCount?: number | null;
  [key: string]: any;
}

interface PropertyDetailProps {
  id?: number;
  isAdmin?: boolean;
  linkCode?: string;
}

export default function PropertyDetail({ id, isAdmin = false, linkCode }: PropertyDetailProps) {
  // Extract route parameters - check both route patterns
  const [, regularParams] = useRoute("/properties/:id");
  const [, linkParams] = useRoute("/properties/:propertyId/link/:linkCode");
  
  // Determine which route we're on and extract the appropriate parameters
  const isLinkRoute = linkParams !== null;
  const routePropertyId = isLinkRoute ? linkParams?.propertyId : regularParams?.id;
  const routeLinkCode = isLinkRoute ? linkParams?.linkCode : undefined;
  
  // Use props if provided, otherwise use route parameters
  const propertyId = id || parseInt(routePropertyId || "0");
  const effectiveLinkCode = linkCode || routeLinkCode;
  
  const { toast } = useToast();
  
  // State for booking
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [guests, setGuests] = useState(1);
  const [specialRequests, setSpecialRequests] = useState("");
  const { user, isLoading: isAuthLoading } = useAuth();
  const [guestCount, setGuestCount] = useState(1);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  
  // --- LINK-BASED CALENDAR PRICING STATE ---
  const [calendarPrices, setCalendarPrices] = useState<Record<string, { price: number | null; status: string }>>({});
  const [calendarPriceRange, setCalendarPriceRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const [visibleMonth, setVisibleMonth] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [isLoadingCalendar, setIsLoadingCalendar] = useState(false);
  const [showAuthForm, setShowAuthForm] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);

  // Google One Tap for guests (like link.tsx)
  useGoogleOneTap(
    async (credential) => {
      try {
        // Send credential to backend to sign in or register guest
        const res = await apiRequest("POST", "auth/google-guest", { credential });
        window.localStorage.setItem("guest_google_signed_in", "true");
        window.localStorage.setItem("guest_info", JSON.stringify(res.user));
        toast({ title: "Signed in as guest", description: res.user?.email || "Welcome!" });
      } catch (err: any) {
        toast({ title: "Google Sign-In failed", description: err.message || "Could not sign in as guest.", variant: "destructive" });
      }
    },
    import.meta.env.VITE_GOOGLE_CLIENT_ID || ""
  );

  // Fetch property data
  const { 
    data: property, 
    isLoading: isPropertyLoading, 
    error: propertyError
  } = useQuery({
    queryKey: effectiveLinkCode ? ["property-link-property", propertyId, effectiveLinkCode] : ["public-properties", propertyId],
    queryFn: async () => {
      if (effectiveLinkCode) {
        // Store linkCode in sessionStorage for downstream API requests
        sessionStorage.setItem("staymiddle_link_code", effectiveLinkCode);
        // Fetch the property for this linkCode
        const res = await apiRequest("GET", `property-links/${effectiveLinkCode}?propertyId=${propertyId}`);
        
        // Debug logging to see what we get from the API
        console.log("🔍 Property link API response:", res);
        console.log("🔍 Looking for propertyId:", propertyId, "type:", typeof propertyId);
        
        // If this is an "all properties link" and we have a specific propertyId, fetch it directly
        if (res.isAllPropertiesLink && propertyId) {
          console.log("🔄 All properties link with specific propertyId, fetching property directly");
          try {
            const propertyRes = await apiRequest("GET", `properties/${propertyId}`);
            console.log("✅ Successfully fetched property via direct API call:", propertyRes.title);
            return propertyRes;
          } catch (error) {
            console.log("❌ Failed to fetch property directly:", error);
            // Fall back to looking in the properties array if direct fetch fails
          }
        }
        
        if (res.properties && Array.isArray(res.properties)) {
          console.log("🔍 Found properties array:", res.properties.map((p: any) => ({ id: p.id, title: p.title })));
          console.log("🔍 All property IDs in response:", res.properties.map((p: any) => `${p.id} (${typeof p.id})`));
          const foundProperty = res.properties.find((p: any) => p.id === propertyId || p.id === propertyId.toString());
          if (foundProperty) {
            console.log("✅ Found property in properties array:", foundProperty.title);
            return foundProperty;
          }
        }
        
        if (res.property) {
          console.log("🔍 Found single property:", { id: res.property.id, title: res.property.title });
          if (res.property.id === propertyId || res.property.id === propertyId.toString()) {
            console.log("✅ Found matching single property");
            return res.property;
          }
        }
        
        // If we have a properties array but didn't find the specific property, return the first one
        if (res.properties && Array.isArray(res.properties) && res.properties.length > 0) {
          console.log("⚠️ Property ID not found, returning first property from array");
          return res.properties[0];
        }
        
        // If we have a single property, return it regardless of ID mismatch
        if (res.property) {
          console.log("⚠️ Returning single property despite ID mismatch");
          return res.property;
        }
        
        console.log("❌ No property found in response");
        return null;
      } else {
        // Remove link code from sessionStorage if not in link mode
        sessionStorage.removeItem("staymiddle_link_code");
        // Always use the public property API for /properties/:id
        const res = await apiRequest('GET', `properties/${propertyId}`);
        return res;
      }
    },
    enabled: propertyId > 0
  });

  // State for availability data with month-by-month caching
  const [availabilityData, setAvailabilityData] = useState<Record<string, any>>({});
  const [availabilityRange, setAvailabilityRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);

  // Fetch availability data for a given range (month-by-month caching like calendar-prices)
  const fetchAvailabilityData = async (startDate: Date, endDate: Date) => {
    if (!propertyId) return;
    
    try {
      setIsLoadingAvailability(true);
      const res = await apiRequest(
        'GET',
        `property-availability/${propertyId}?startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}`
      );
      

      
      // Transform array to object keyed by date for quick lookup
      const dataMap: Record<string, any> = {};
      (res || []).forEach((entry: any) => {
        if (entry.date) {
          const dateKey = entry.date.split('T')[0]; // Extract YYYY-MM-DD
          dataMap[dateKey] = entry;
        }
      });
      
      setAvailabilityData(prev => ({ ...prev, ...dataMap }));
      setAvailabilityRange({ startDate, endDate });
    } catch (err) {
      console.error('Failed to fetch availability data:', err);
    } finally {
      setIsLoadingAvailability(false);
    }
  };

  // Handle calendar month change - fetch availability for that month + buffer
  const handleAvailabilityMonthChange = (date: Date) => {
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    
    // Check if we already have data for this month range
    if (availabilityRange) {
      const isWithinRange = date >= availabilityRange.startDate && date <= availabilityRange.endDate;
      if (isWithinRange) {
        // Data already loaded for this range
        return;
      }
    }
    
    // Fetch data for current month + 2 months ahead for performance
    const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const endDate = new Date(date.getFullYear(), date.getMonth() + 3, 0);
    fetchAvailabilityData(startDate, endDate);
  };

  // Legacy bookings for backward compatibility
  const bookings = Object.values(availabilityData);
  const isBookingsLoading = isLoadingAvailability;

  // Create booking mutation
  const createBookingMutation = useMutation({
    mutationFn: async (data: any) => {
      try {
        const res = await apiRequest('POST', 'bookings', data);
        if (!res) {
          const errorData = res;
          
          // Enhanced error handling for unavailable dates
          if (errorData.message && errorData.message.includes("unavailable")) {
            // If the server provides specific blocked dates, format them for display
            if (errorData.dates && Array.isArray(errorData.dates)) {
              const formattedDates = errorData.dates.map((date: string) => 
                new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
              ).join(', ');
              
              throw new Error(`${errorData.message}. Blocked dates: ${formattedDates}`);
            }
            
            // If source info is included (like from Airbnb, etc.), use it
            if (errorData.message.includes("from")) {
              throw new Error(errorData.message);
            }
            
            // Default enhanced message
            throw new Error(`${errorData.message}. This may be due to an external calendar import (Airbnb, Booking.com).`);
          }
          
          throw new Error(errorData.message || 'Failed to create booking');
        }
        // return res.json();
        return res;
      } catch (err: any) {
        // Handle network errors
        if (!err.message || err.message === 'Failed to fetch') {
          throw new Error('Network error. Please check your connection and try again.');
        }
        throw err;
      }
    },
    onSuccess: (booking) => {
      toast({
        title: "Booking created!",
        description: "Please complete payment to confirm your reservation.",
        variant: "default",
      });
      
      // Reset state
      setStartDate(undefined);
      setEndDate(undefined);
      setGuests(1);
      setSpecialRequests("");
      
      // Redirect to payment page
      window.location.href = `/bookings/${booking.id}/confirmation`;
      
      // Clear availability cache to refresh data
      setAvailabilityData({});
      setAvailabilityRange(null);
      // Re-fetch current month availability 
      if (property?.id) {
        handleAvailabilityMonthChange(visibleMonth);
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Booking failed",
        description: error.message,
        variant: "destructive",
      });
      
      // If the error is related to unavailability, reset the date selection
      if (error.message.includes("unavailable")) {
        setStartDate(undefined);
        setEndDate(undefined);
        
        // Clear availability cache to refresh the availability data
        setAvailabilityData({});
        setAvailabilityRange(null);
        // Re-fetch current month availability 
        if (property?.id) {
          handleAvailabilityMonthChange(visibleMonth);
        }
      }
    }
  });
  
  // Handle date selection
  const handleDateSelect = (date: Date | undefined) => {
    if (!startDate) {
      setStartDate(date);
    } else if (!endDate && date && isAfter(date, startDate)) {
      setEndDate(date);
    } else {
      setStartDate(date);
      setEndDate(undefined);
    }
  };
  
  // Calculate number of nights and total price
  const nights = startDate && endDate ? differenceInCalendarDays(endDate, startDate) : 0;
  const totalPrice = (property && property.basePrice) ? nights * property.basePrice : 0;

  // Validation functions for booking constraints
  const getCheckInConstraints = (checkInDate: Date) => {
    if (!availabilityData || !checkInDate) return { minNights: 1, maxNights: 365 };
    
    const dateStr = format(checkInDate, 'yyyy-MM-dd');
    const dayData = availabilityData[dateStr]; // Use object key lookup instead of find
    
    return {
      minNights: dayData?.minNights || 1,
      maxNights: dayData?.maxNights || 365
    };
  };

  const validateGuestCount = (count: number) => {
    if (!property) return true;
    return count >= 1 && count <= property.maxGuests;
  };

  const validateStayDuration = (checkIn: Date, checkOut: Date) => {
    if (!checkIn || !checkOut) return { isValid: true, message: '' };
    
    const nights = differenceInCalendarDays(checkOut, checkIn);
    const constraints = getCheckInConstraints(checkIn);
    
    if (nights < constraints.minNights) {
      return {
        isValid: false,
        message: `Minimum stay is ${constraints.minNights} night${constraints.minNights > 1 ? 's' : ''} for this check-in date`
      };
    }
    
    if (nights > constraints.maxNights) {
      return {
        isValid: false,
        message: `Maximum stay is ${constraints.maxNights} night${constraints.maxNights > 1 ? 's' : ''} for this check-in date`
      };
    }
    
    return { isValid: true, message: '' };
  };

  // Get validation messages for current booking
  const guestValidation = validateGuestCount(guestCount);
  const stayValidation = dateRange.from && dateRange.to ? validateStayDuration(dateRange.from, dateRange.to) : { isValid: true, message: '' };
  const isBookingValid = guestValidation && stayValidation.isValid;

  // Reset guest count if it exceeds property maxGuests when property data loads
  useEffect(() => {
    if (property && property.maxGuests && guestCount > property.maxGuests) {
      setGuestCount(property.maxGuests);
    }
  }, [property?.maxGuests]);

  // Show toast message for validation errors when they occur
  useEffect(() => {
    if (dateRange.from && dateRange.to && !stayValidation.isValid) {
      toast({
        title: "Invalid Stay Duration",
        description: stayValidation.message,
        variant: "destructive",
      });
    }
  }, [stayValidation.isValid, stayValidation.message, dateRange.from, dateRange.to]);
  
  // Handle booking submission
  const handleBookingSubmit = () => {
    if (!startDate || !endDate || !property || !user) return;
    
    // Calculate amounts based on the booking
    const baseAmount = property.basePrice * nights * 100; // Convert to cents
    const markupAmount = 0; // Default
    const platformFeeAmount = Math.round(baseAmount * 0.05); // 5% platform fee
    const totalAmountInCents = baseAmount + markupAmount + platformFeeAmount;
    
    createBookingMutation.mutate({
      propertyId: property.id,
      guestId: user.id,
      checkIn: startDate.toISOString(),
      checkOut: endDate.toISOString(),
      guestCount: guests,
      baseAmount: baseAmount,
      markupAmount: markupAmount,
      platformFeeAmount: platformFeeAmount,
      totalAmount: totalAmountInCents,
      guestName: user.fullName || user.username,
      guestEmail: user.email,
      guestPhone: user.phone || '',
      specialRequests: specialRequests,
      // Status and stripePaymentIntentId will be set by server
    });
  };
  
  const isLoading = isPropertyLoading || isBookingsLoading;
  
  // Helper function to render content with the appropriate layout
  const renderWithLayout = (content: React.ReactNode) => {
    if (isAdmin) {
      return <AdminLayout>{content}</AdminLayout>;
    } else {
      return <Layout>{content}</Layout>;
    }
  };

  // Return URL for Back button
  const getReturnUrl = () => {
    if (isAdmin) {
      return "/admin/properties";
    } else {
      return "/properties";
    }
  };

  // Determine if user can book/select dates: always true for linkCode, or for manager/agent
  const userRole = user?.role;
  const canSelectDates = !!effectiveLinkCode || userRole === 'manager' || userRole === 'agent';

  // UNIFIED booking calculation - always use dateRange for consistency
  const bookingNights = dateRange.from && dateRange.to
    ? differenceInCalendarDays(dateRange.to, dateRange.from)
    : 0;
  const bookingSubtotal = effectiveLinkCode 
    ? totalAmount // Use calculated total from pricing API
    : (property && property.basePrice ? bookingNights * property.basePrice : 0); // Use base price calculation
  
  // Calculate 1.5% guest platform fee
  const platformFeePercentage = 1.5;
  const platformFee = Math.round(bookingSubtotal * (platformFeePercentage / 100));
  const bookingTotal = bookingSubtotal + platformFee;

  // Fetch per-day prices for the visible calendar range (linkCode only)
  const fetchCalendarPrices = async (startDate: Date, endDate: Date) => {
    if (!effectiveLinkCode) return;
    try {
      setIsLoadingCalendar(true);
      const res = await apiRequest(
        "GET",
        `calendar-prices/${property.id}/link/${effectiveLinkCode}/?startDate=${startDate.toISOString().split("T")[0]}&endDate=${endDate.toISOString().split("T")[0]}`
      );
      // Transform array to object keyed by date
      const priceMap: Record<string, { price: number | null; status: string }> = {};
      (res || []).forEach((day: any) => {
        priceMap[day.date] = { price: day.finalPrice, status: day.status };
      });
      setCalendarPrices(priceMap);
      setCalendarPriceRange({ startDate, endDate });
    } catch (err) {
      setCalendarPrices({});
      setCalendarPriceRange(null);
    } finally {
      setIsLoadingCalendar(false);
    }
  };

  // On mount and when property changes, load availability and prices for extended range
  useEffect(() => {
    if (property?.id) {
      const now = new Date();
      setVisibleMonth(now);
      
      // Always fetch availability data
      handleAvailabilityMonthChange(now);
      
      // Also fetch pricing data if linkCode exists
      if (effectiveLinkCode) {
        // Fetch prices for previous month, current month, and next 2 months to cover full calendar view
        const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endDate = new Date(now.getFullYear(), now.getMonth() + 3, 0);
        fetchCalendarPrices(startDate, endDate);
      }
    }
    // eslint-disable-next-line
  }, [property?.id, effectiveLinkCode]);

  // UNIFIED month change handler - works for both link and non-link cases
  const handleMonthChange = (date: Date) => {
    setVisibleMonth(date);
    
    // Always fetch availability data for month change
    handleAvailabilityMonthChange(date);
    
    // Also fetch pricing data if linkCode exists
    if (effectiveLinkCode) {
      // Include previous and next months to cover all visible calendar days
      const startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
      const endDate = new Date(date.getFullYear(), date.getMonth() + 2, 0);
      fetchCalendarPrices(startDate, endDate);
    }
  };

  // Calculate total amount for selected range (linkCode only)
  useEffect(() => {
    if (effectiveLinkCode && dateRange.from && dateRange.to) {
      const days = eachDayOfInterval({ start: dateRange.from, end: addDays(dateRange.to, -1) }); // exclude checkout day
      let sum = 0;
      days.forEach(date => {
        const price = getNumericPrice(date);
        if (typeof price === "number" && !isNaN(price)) {
          sum += price;
        }
      });
      setTotalAmount(sum);
      console.log("[DEBUG] calendarPrices:", calendarPrices, "dateRange:", dateRange, "total:", sum);
    }
  }, [dateRange, calendarPrices, effectiveLinkCode]);

  // UNIFIED availability function - now uses stored Airbnb fields for cleaner logic
  const isDateAvailable = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    // Use bookings data for availability checking (same logic regardless of link code)
    const isBlocked = bookings?.some((entry: any) => {
      // Match the date first
      const entryDateStr = entry.date || entry.checkIn;
      const entryDate = new Date(entryDateStr);
      const normalizedEntryDate = entryDate.toISOString().split('T')[0];
      
      if (dateStr !== normalizedEntryDate) {
        return false; // This entry doesn't match our date
      }
      
      // Use the stored Airbnb fields for three-state system
      if (entry.source === 'airbnb' && entry.availableForCheckin !== undefined) {
        // Simple logic: block if NOT available for check-in
        // GREEN: availableForCheckin=true
        // ORANGE/RED: availableForCheckin=false (orange vs red determined by isOccupiedDate)
        return entry.availableForCheckin !== true;
      }
      
      // Legacy logic for non-Airbnb entries
      if (entry.type === 'booking' && entry.checkIn && entry.checkOut) {
        const start = new Date(entry.checkIn);
        const end = new Date(entry.checkOut);
        const startStr = format(start, 'yyyy-MM-dd');
        const endStr = format(end, 'yyyy-MM-dd');
        return dateStr >= startStr && dateStr < endStr;
      }
      
      // Fallback for other types
      return entry.status === 'blocked' || entry.status === 'booked';
    });

    return !isBlocked;
  };

  // Helper: is the date the end of the selected range (check-out)
  const isEndDate = (date: Date) => {
    return dateRange.to && isSameDay(date, dateRange.to);
  };

  // Helper: is the date occupied but allows checkout (using stored Airbnb fields)
  const isOccupiedDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    const result = bookings?.some((entry: any) => {
      // Match the date first
      const entryDateStr = entry.date || entry.checkIn;
      const entryDate = new Date(entryDateStr);
      const normalizedEntryDate = entryDate.toISOString().split('T')[0];
      
      if (dateStr !== normalizedEntryDate) {
        return false; // This entry doesn't match our date
      }
      
      // Use stored Airbnb fields for cleaner logic
      if (entry.source === 'airbnb' && entry.availableForCheckout !== undefined) {
        // A date is occupied (light green) if:
        // - It's not available for check-in (availableForCheckin: false)
        // - BUT it allows checkout (availableForCheckout: true)
        // - Regardless of status (can be 'booked' or 'blocked')
        return entry.availableForCheckin === false && entry.availableForCheckout === true;
      }
      
      // Legacy logic for non-Airbnb entries
      return entry.status === 'booked' && (entry.source === 'airbnb' || entry.source === 'external');
    });

    return !!result;
  };

  // Get price for a date (linkCode only)
  const getDatePrice = (date: Date) => {
    const key = format(date, "yyyy-MM-dd");
    
    if (calendarPrices[key]) {
      return calendarPrices[key].price;
    }
    return null;
  };

  // Get numeric price for calculations
  const getNumericPrice = (date: Date) => {
    const key = format(date, "yyyy-MM-dd");
    
    if (calendarPrices[key]) {
      return calendarPrices[key].price;
    }
    return property.basePrice || 300; // Fallback to base price
  };

  // UNIFIED calendar day renderer - works for both link and non-link cases
  const CalendarDay = ({ date, ...restProps }: { date: Date; [key: string]: any }) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const isAvailable = isDateAvailable(date); // Uses unified availability logic
    const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
    const price = effectiveLinkCode ? getDatePrice(date) : null; // Only fetch price if link code exists
    const noPricing = effectiveLinkCode && price === null;
    const isOccupied = isOccupiedDate(date); // Check if occupied but allows checkout
    
    // Determine the day status
    const isBlocked = !isAvailable && !isPast && !isOccupied; // Only truly blocked dates
    const isSelected =
      dateRange.from &&
      dateRange.to &&
      (isAvailable || isOccupied) && // Allow selection on available or occupied dates
      isWithinInterval(date, { start: dateRange.from, end: dateRange.to }) &&
      eachDayOfInterval({ start: dateRange.from, end: dateRange.to }).every(d => isDateAvailable(d) || isOccupiedDate(d));
    const isWeekend = date.getDay() === 0 || date.getDay() === 6;
    
    // Filter out non-DOM props to avoid React warnings
    const { displayMonth, month, activeModifiers, modifiers, selected, ...domProps } = restProps;
    
    return (
      <div
        {...domProps}
        className={cn(
          restProps.className,
          "w-full h-full rounded-md flex flex-col items-center justify-center text-xs font-medium transition-all p-1",
          // Past dates
          isPast && "bg-gray-400 text-gray-600 rounded-md opacity-60",
          // Blocked dates - force strong red even when disabled
          isBlocked && "!bg-red-800 !text-white !border !border-red-800 !font-bold rounded-md !opacity-100",
          // Occupied dates (checkout-only) - light green color
          isOccupied && !isPast && !isSelected && "bg-green-300 text-green-800 rounded-md border border-green-400",
          // No pricing (for link-based) - light gray 
          noPricing && !isPast && !isBlocked && !isOccupied && "bg-gray-200 text-gray-500 rounded-md opacity-70",
          // Available dates
          !isPast && !isBlocked && !isOccupied && !noPricing && !isSelected && "bg-green-500 text-green-700",
          // Weekend available dates
          isWeekend && !isPast && isAvailable && !isSelected && !noPricing && !isOccupied && "bg-green-600 text-green-800",
          // Selected dates
          isSelected && "!bg-yellow-300 !text-black !border-2 !border-yellow-500 rounded-md",
          // Hover effects only for selectable dates
          !isPast && (isAvailable || isOccupied) && !noPricing && "hover:bg-yellow-50"
        )}
        style={{
          // Force strong red color for blocked dates even when disabled
          ...(isBlocked && {
            backgroundColor: '#991b1b !important',
            color: 'white !important',
            borderColor: '#991b1b !important',
            opacity: '1 !important'
          })
        }}
      >
        <time dateTime={format(date, "yyyy-MM-dd")}
          className="text-[10px] font-normal leading-tight">
          {format(date, "d")}
        </time>
        {/* Show price only for link-based access on available (not occupied), non-past dates with pricing */}
        {effectiveLinkCode && isAvailable && !isOccupied && !isPast && !noPricing && price !== undefined && (
          <div className="text-[10px] font-bold min-h-[16px] flex items-center justify-center w-full text-green-800">
            {price}
          </div>
        )}
      </div>
    );
  };

  // Add state for showing booking form (like link.tsx)
  const [showBookingForm, setShowBookingForm] = useState(false);

  // Prepare nightlyPrices array for the selected date range (linkCode only)
  const nightlyPrices = effectiveLinkCode && dateRange.from && dateRange.to
    ? eachDayOfInterval({ start: dateRange.from, end: addDays(dateRange.to, -1) }).map(date => {
        const key = format(date, "yyyy-MM-dd");
        return { date: key, price: getNumericPrice(date) };
      })
    : [];

  // Helper functions for role-based pricing
  const getMarkup = (basePrice: number) => Math.round(basePrice * 0.1); // 10% markup
  const getPlatformFee = (basePrice: number) => Math.round(basePrice * 0.05); // 5% platform fee
  const getDisplayPrice = (basePrice: number, user: any) => {
    if (!user) {
      // Guest: basePrice + markup + platformFee
      return basePrice + getMarkup(basePrice) + getPlatformFee(basePrice);
    }
    
    const role = user.role?.toLowerCase();
    switch (role) {
      case 'manager':
        return basePrice; // Manager sees base price only
      case 'agent':
        return basePrice + getMarkup(basePrice); // Agent sees base + markup
      default:
        return basePrice + getMarkup(basePrice) + getPlatformFee(basePrice); // Guest pricing
    }
  };

  if (isLoading) {
    return renderWithLayout(
      <div className="container py-12 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
          <p className="text-lg text-muted-foreground">Loading property details...</p>
        </div>
      </div>
    );
  }
  
  if (propertyError || !property) {
    return renderWithLayout(
      <div className="container py-12">
        <div className="max-w-2xl mx-auto text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Property Not Found</h1>
          <p className="text-muted-foreground mb-6">The property you're looking for doesn't exist or has been removed.</p>
          <Link href={getReturnUrl()}>
            <Button>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Properties
            </Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const content = (
    <div className="container py-8">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-muted-foreground mb-6">
        <Link href={getReturnUrl()}>
          <span className="hover:text-primary cursor-pointer">Properties</span>
        </Link>
        <ChevronLeft className="h-4 w-4 mx-2 rotate-180" />
        <span>{property.title}</span>
      </div>
      
      {/* Property Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{property.title}</h1>
        <p className="text-muted-foreground flex items-center">
          <MapPin className="h-4 w-4 mr-1" />
          {property.area}, {property.address}
        </p>
      </div>
      
      <div className="mb-4">
        {/* Location Title Only */}
        {property.locationTitle && (
          <div className="text-lg font-semibold mb-1 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-primary" />
            {property.locationTitle}
          </div>
        )}
      </div>
      
      {/* Desktop: Images and Calendar Side by Side, Mobile: Images then Calendar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        {/* Images Section */}
        <div className="lg:col-span-2">
          {property.images && property.images.length > 0 ? (
            <PropertyImageGallery 
              images={[...property.images].sort((a, b) => (b.isPrimary === true ? 1 : 0) - (a.isPrimary === true ? 1 : 0))}
              title={property.title}
            />
          ) : (
            <div className="aspect-video rounded-lg overflow-hidden bg-muted mb-4 flex items-center justify-center">
              <p className="text-muted-foreground">No images available</p>
            </div>
          )}
          
          {/* Rating and Review Count */}
          {(property.rating || property.reviewCount) && (
            <div className="flex items-center gap-2 mt-2">
              {property.rating && (
                <span className="flex items-center text-yellow-600 font-semibold">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.967a1 1 0 00.95.69h4.175c.969 0 1.371 1.24.588 1.81l-3.38 2.455a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.54 1.118l-3.38-2.454a1 1 0 00-1.175 0l-3.38 2.454c-.784.57-1.838-.196-1.54-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.05 9.394c-.783-.57-.38-1.81.588-1.81h4.175a1 1 0 00.95-.69l1.286-3.967z" /></svg>
                  {property.rating}
                </span>
              )}
              {property.reviewCount && (
                <span className="text-muted-foreground">({property.reviewCount} reviews)</span>
              )}
            </div>
          )}
        </div>
        
        {/* Desktop Calendar Section */}
        <div className="hidden lg:block">
          <Card>
            <CardHeader>
              {canSelectDates ? (
                <>
                  <CardTitle className="flex justify-between items-center">
                    <span>{canSelectDates ? "Price by date" : `AED ${property.basePrice}`}</span>
                    <span className="text-sm font-normal text-muted-foreground">per night</span>
                  </CardTitle>
                  <CardDescription>
                    Select check-in and check-out dates
                  </CardDescription>
                </>
              ) : (
                <>
                  <CardTitle>Contact for Booking</CardTitle>
                  <CardDescription>
                    Price information is available through the property links
                  </CardDescription>
                </>
              )}
            </CardHeader>
            <CardContent>
              <div className="property-detail-calendar">
                {/* UNIFIED Calendar - uses same availability logic, only pricing differs */}
                {(effectiveLinkCode && isLoadingCalendar) ? (
                  <div className="p-4 flex items-center justify-center border rounded-md">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="w-full overflow-x-auto">
                    <Calendar
                      month={visibleMonth}
                      mode="range"
                      selected={dateRange}
                      onSelect={(range: DateRange | undefined) => {
                        if (!range) {
                          setDateRange({ from: undefined, to: undefined });
                          return;
                        }
                        const { from, to } = range;
                        
                        // If we have both from and to dates, validate that all dates in between are available
                        if (from && to) {
                          // Prevent same check-in and check-out
                          if (isSameDay(from, to)) {
                            const nextDay = addDays(from, 1);
                            if (isDateAvailable(nextDay)) {
                              setDateRange({ from, to: nextDay });
                            } else {
                              setDateRange({ from: undefined, to: undefined });
                            }
                            return;
                          }
                          
                          // Check if all dates in the range are available using unified logic
                          const datesInRange = eachDayOfInterval({ start: from, end: addDays(to, -1) }); // exclude checkout day
                          const allDatesAvailable = datesInRange.every(date => isDateAvailable(date));
                          
                          if (allDatesAvailable) {
                            setDateRange(range);
                          } else {
                            // Find the first blocked date in the range and set range up to that date
                            const firstBlockedIndex = datesInRange.findIndex(date => !isDateAvailable(date));
                            if (firstBlockedIndex > 0) {
                              // Set range from start up to the day before the blocked date
                              const newTo = datesInRange[firstBlockedIndex - 1];
                              setDateRange({ from, to: addDays(newTo, 1) }); // add 1 day for checkout
                            } else {
                              // The first date itself is blocked, clear selection
                              setDateRange({ from: undefined, to: undefined });
                            }
                          }
                        } else {
                          // Only from date selected, or partial selection
                          setDateRange(range);
                        }
                      }}
                      numberOfMonths={1}
                      disabled={(date: Date) => {
                        const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                        const isAvailable = isDateAvailable(date);
                        const isOccupiedButCheckoutAllowed = isOccupiedDate(date);
                        
                        // For link-based bookings, disable dates without pricing (but not checkout-only dates)
                        const noPricing = effectiveLinkCode && getDatePrice(date) === null && !isOccupiedButCheckoutAllowed;
                        
                        // Allow selection if:
                        // 1. Date is available for check-in/checkout
                        // 2. Date is occupied but allows checkout (checkout-only dates)
                        // 3. Date is the currently selected end date
                        const canSelect = isAvailable || isOccupiedButCheckoutAllowed || isEndDate(date);
                        
                        return isPast || !canSelect || Boolean(noPricing);
                      }}
                      components={{ DayContent: CalendarDay as any }}
                      onMonthChange={handleMonthChange}
                      className="rdp w-full max-w-full rounded-lg overflow-hidden"
                    />
                    {/* Calendar Legend for Desktop */}
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-500 rounded"></div>
                        <span>Available</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-300 border border-green-400 rounded"></div>
                        <span>Checkout Only</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-800 rounded"></div>
                        <span>Blocked</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-yellow-300 border border-yellow-500 rounded"></div>
                        <span>Selected</span>
                      </div>
                    </div>
                  </div>
                )}
                              <style>
              {`
                .property-detail-calendar .rdp-day_disabled {
                  opacity: 1 !important;
                  pointer-events: none !important;
                }
                
                /* Allow pointer events for light green (checkout-allowed) dates */
                .property-detail-calendar .rdp-day_disabled .bg-green-300 {
                  pointer-events: auto !important;
                }
                
                /* Ensure blocked dates stay red and visible even when disabled */
                .property-detail-calendar .rdp-day_disabled .bg-red-800 {
                  background-color: #991b1b !important;
                  color: white !important;
                  opacity: 1 !important;
                }
                
                /* Target the disabled state specifically for blocked dates */
                .property-detail-calendar .rdp-day_disabled [class*="bg-red"] {
                  opacity: 1 !important;
                  filter: none !important;
                }
              `}
              </style>
              </div>

              {/* UNIFIED Date Display and Booking Summary */}
              {(dateRange.from && dateRange.to) && (
                <div className="mt-4 p-3 bg-muted rounded-md text-sm">
                  <div className="flex justify-between items-center mb-2">
                    <span>Check-in:</span>
                    <span className="font-medium">
                      {dateRange.from ? format(dateRange.from, 'dd MMM yyyy') : 'Select date'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>Check-out:</span>
                    <span className="font-medium">
                      {dateRange.to ? format(dateRange.to, 'dd MMM yyyy') : 'Select date'}
                    </span>
                  </div>
                  {bookingNights > 0 && (
                    <>
                      <div className="flex justify-between items-center mb-2">
                        <span>Duration:</span>
                        <span className="font-medium">
                          {bookingNights} {bookingNights === 1 ? 'night' : 'nights'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span>{effectiveLinkCode ? 'Subtotal' : `AED ${property.basePrice} × ${bookingNights}`}:</span>
                        <span className="font-medium">
                          {effectiveLinkCode ? formatCurrency(bookingSubtotal) : `AED ${property.basePrice * bookingNights}`}
                        </span>
                      </div>
                      {bookingSubtotal > 0 && (
                        <div className="flex justify-between items-center mb-2">
                          <span>Platform fee ({platformFeePercentage}%):</span>
                          <span className="font-medium">
                            {effectiveLinkCode ? formatCurrency(platformFee) : `AED ${platformFee}`}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center border-t pt-2 font-medium">
                        <span>Total:</span>
                        <span>{effectiveLinkCode ? formatCurrency(bookingTotal) : `AED ${bookingTotal}`}</span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Booking Button and Guest Auth/Booking Form */}
              {!isAdmin && canSelectDates && (
                 <> 
                  {/* If not logged in and linkCode, show guest auth form */}
                  {/* {linkCode && !user && showAuthForm && ( */}
                  {(dateRange.from && dateRange.to) && (
                        <div className="mt-4 space-y-2">
                        <label className="text-sm font-medium">Number of Guests</label>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setGuestCount(prev => Math.max(1, prev - 1))}
                            disabled={guestCount <= 1}
                          >
                            -
                          </Button>
                          <span className="w-8 text-center">{guestCount}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setGuestCount(prev => property?.maxGuests ? Math.min(property.maxGuests, prev + 1) : prev + 1)}
                            disabled={property?.maxGuests ? guestCount >= property.maxGuests : false}
                          >
                            +
                          </Button>
                        </div>
                        {property && (
                          <p className="text-xs text-muted-foreground">
                            Maximum {property.maxGuests} guest{property.maxGuests > 1 ? 's' : ''} allowed
                          </p>
                        )}
                        {!guestValidation && (
                          <p className="text-xs text-red-500">
                            Please select between 1 and {property?.maxGuests || 1} guests
                          </p>
                        )}
                        </div>
                        )}
                  {!user && showAuthForm ? (
                  <div className="mt-4 p-4 border rounded-md bg-muted/30">
                    <h3 className="text-lg font-semibold mb-2">Sign in or Register to Book</h3>
                    <GuestAuthForm 
                      redirectPath={window.location.pathname + window.location.search}
                      onSuccess={() => setShowAuthForm(false)}
                    />
                  </div>
                  ) : (
                  <>
                    {/* Show validation errors */}
                    {dateRange.from && dateRange.to && !stayValidation.isValid && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                        {stayValidation.message}
                      </div>
                    )}

                    <Button 
                      className="w-full mt-4"
                      onClick={() => {
                        if (!user) {
                          setShowAuthForm(true);
                        } else {
                          setShowBookingForm(true);
                        }
                      }}
                      disabled={!dateRange.from || !dateRange.to || isAuthLoading || !isBookingValid}
                    >
                      {isAuthLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Processing...
                        </>
                      ) : !isBookingValid && dateRange.from && dateRange.to ? (
                        "Invalid Booking"
                      ) : (
                        "Book Now"
                      )}
                    </Button>
                  </>
                  )}

                  {(showBookingForm && dateRange.from && dateRange.to && (
                  <div className="mt-4 space-y-4">
                      <BookingForm
                        propertyId={property.id}
                        checkInDate={dateRange.from}
                        checkOutDate={dateRange.to}
                        guestCount={guestCount}
                        nightlyPrices={nightlyPrices}
                        totalAmount={bookingTotal}
                        agentId={property.agentId}
                        agentLinkId={property.linkId}
                        onBookingCreated={(bookingId: number) => {
                          window.location.href = `/bookings/${bookingId}/confirmation`;
                        }}
                      />
                  </div>
                  ))}
              </>
              )}

              {/* Admin Edit Button */}
              {isAdmin  && (
                <div className="mt-4 flex flex-col gap-2">
                  <Link href={`/admin/properties/${propertyId}/edit`}>
                    <Button className="w-full">
                      Edit Property
                    </Button>
                  </Link>
                  {property.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 hover:text-green-800"
                      >
                        <Check className="h-4 w-4 mr-2" /> Approve
                      </Button>
                      <Button 
                        variant="outline" 
                        className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800"
                      >
                        <X className="h-4 w-4 mr-2" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
        
      {/* Property Specs Row (with icons) */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="border rounded-lg p-4 text-center flex flex-col items-center">
          <Bed className="h-6 w-6 mb-2 text-primary" />
          <p className="text-lg font-semibold">{property.bedrooms === 0 ? "Studio" : property.bedrooms}</p>
          <p className="text-sm text-muted-foreground">{property.bedrooms === 0 ? "" : "Bedrooms"}</p>
        </div>
        <div className="border rounded-lg p-4 text-center flex flex-col items-center">
          <ShowerHead className="h-6 w-6 mb-2 text-primary" />
          <span className="text-lg font-bold">{Number(property.bathrooms).toFixed(1)}</span>
          <span className="text-sm text-muted-foreground">Bathrooms</span>
        </div>
        <div className="border rounded-lg p-4 text-center flex flex-col items-center">
          <Users className="h-6 w-6 mb-2 text-primary" />
          <p className="text-lg font-semibold">{property.maxGuests}</p>
          <p className="text-sm text-muted-foreground">Max Guests</p>
        </div>
      </div>

      {/* Mobile Calendar - Show below images, above description on mobile/tablet only */}
      <div className="lg:hidden mb-8">
        <Card>
          <CardHeader>
            {canSelectDates ? (
              <>
                <CardTitle className="flex justify-between items-center">
                  <span>Price by date</span>
                  <span className="text-sm font-normal text-muted-foreground">per night</span>
                </CardTitle>
                <CardDescription>
                  Select check-in and check-out dates
                </CardDescription>
              </>
            ) : (
              <>
                <CardTitle>Contact for Booking</CardTitle>
                <CardDescription>
                  Price information is available through the property links
                </CardDescription>
              </>
            )}
          </CardHeader>
          <CardContent className="p-4">
            <div className="property-detail-calendar">
              {/* UNIFIED Calendar - uses same availability logic, only pricing differs */}
              {(effectiveLinkCode && isLoadingCalendar) ? (
                <div className="p-4 flex items-center justify-center border rounded-md">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <div className="w-full">
                  <Calendar
                    month={visibleMonth}
                    mode="range"
                    selected={dateRange}
                    onSelect={(range: DateRange | undefined) => {
                      if (!range) {
                        setDateRange({ from: undefined, to: undefined });
                        return;
                      }
                      const { from, to } = range;
                      
                      // If we have both from and to dates, validate that all dates in between are available
                      if (from && to) {
                        // Prevent same check-in and check-out
                        if (isSameDay(from, to)) {
                          const nextDay = addDays(from, 1);
                          if (isDateAvailable(nextDay)) {
                            setDateRange({ from, to: nextDay });
                          } else {
                            setDateRange({ from: undefined, to: undefined });
                          }
                          return;
                        }
                        
                        // Check if all dates in the range are available using unified logic
                        const datesInRange = eachDayOfInterval({ start: from, end: addDays(to, -1) }); // exclude checkout day
                        const allDatesAvailable = datesInRange.every(date => isDateAvailable(date));
                        
                        if (allDatesAvailable) {
                          setDateRange(range);
                        } else {
                          // Find the first blocked date in the range and set range up to that date
                          const firstBlockedIndex = datesInRange.findIndex(date => !isDateAvailable(date));
                          if (firstBlockedIndex > 0) {
                            // Set range from start up to the day before the blocked date
                            const newTo = datesInRange[firstBlockedIndex - 1];
                            setDateRange({ from, to: addDays(newTo, 1) }); // add 1 day for checkout
                          } else {
                            // The first date itself is blocked, clear selection
                            setDateRange({ from: undefined, to: undefined });
                          }
                        }
                      } else {
                        // Only from date selected, or partial selection
                        setDateRange(range);
                      }
                    }}
                    numberOfMonths={1}
                    disabled={(date: Date) => {
                      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                      const isAvailable = isDateAvailable(date);
                      const isOccupiedButCheckoutAllowed = isOccupiedDate(date);
                      const dateStr = format(date, 'yyyy-MM-dd');
                      
                      // Check if date has blocked status but allows checkout
                      const isBlockedButCheckoutAllowed = bookings?.some((entry: any) => {
                        const entryDateStr = entry.date || entry.checkIn;
                        const entryDate = new Date(entryDateStr);
                        const normalizedEntryDate = entryDate.toISOString().split('T')[0];
                        
                        if (dateStr !== normalizedEntryDate) return false;
                        
                        // If blocked status but checkout allowed, treat as selectable
                        return entry.status === 'blocked' && entry.availableForCheckout === true;
                      });
                      
                      // For link-based bookings, disable dates without pricing (but not checkout-only dates)
                      const noPricing = effectiveLinkCode && getDatePrice(date) === null && 
                                       !isOccupiedButCheckoutAllowed && !isBlockedButCheckoutAllowed;
                      
                      // Allow selection if:
                      // 1. Date is available for check-in/checkout
                      // 2. Date is occupied but allows checkout 
                      // 3. Date is blocked but allows checkout
                      // 4. Date is the currently selected end date
                      const canSelect = isAvailable || isOccupiedButCheckoutAllowed || isBlockedButCheckoutAllowed || isEndDate(date);
                      
                      return isPast || !canSelect || Boolean(noPricing);
                    }}
                    components={{ DayContent: CalendarDay as any }}
                    onMonthChange={handleMonthChange}
                    className="rdp w-full rounded-lg overflow-hidden"
                  />
                  
                  {/* Calendar Legend */}
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded"></div>
                      <span>Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-300 border border-green-400 rounded"></div>
                      <span>Checkout Only</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-800 rounded"></div>
                      <span>Blocked</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-yellow-300 border border-yellow-500 rounded"></div>
                      <span>Selected</span>
                    </div>
                  </div>
                </div>
              )}
                          <style>
            {`
              .property-detail-calendar .rdp-day_disabled {
                opacity: 1 !important;
                pointer-events: none !important;
              }
              
              /* Allow pointer events for light green (checkout-allowed) dates */
              .property-detail-calendar .rdp-day_disabled .bg-green-300 {
                pointer-events: auto !important;
              }
              
              /* Ensure blocked dates stay red and visible even when disabled */
              .property-detail-calendar .rdp-day_disabled .bg-red-800 {
                background-color: #991b1b !important;
                color: white !important;
                opacity: 1 !important;
              }
              
              /* Target the disabled state specifically for blocked dates */
              .property-detail-calendar .rdp-day_disabled [class*="bg-red"] {
                opacity: 1 !important;
                filter: none !important;
              }
            `}
            </style>
            </div>

            {/* UNIFIED Date Display and Booking Summary */}
            {(dateRange.from && dateRange.to) && (
              <div className="mt-4 p-3 bg-muted rounded-md text-sm">
                <div className="flex justify-between items-center mb-2">
                  <span>Check-in:</span>
                  <span className="font-medium">
                    {dateRange.from ? format(dateRange.from, 'dd MMM yyyy') : 'Select date'}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span>Check-out:</span>
                  <span className="font-medium">
                    {dateRange.to ? format(dateRange.to, 'dd MMM yyyy') : 'Select date'}
                  </span>
                </div>
                {bookingNights > 0 && (
                  <>
                    <div className="flex justify-between items-center mb-2">
                      <span>Duration:</span>
                      <span className="font-medium">
                        {bookingNights} {bookingNights === 1 ? 'night' : 'nights'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span>{effectiveLinkCode ? 'Subtotal' : `AED ${property.basePrice} × ${bookingNights}`}:</span>
                      <span className="font-medium">
                        {effectiveLinkCode ? formatCurrency(bookingSubtotal) : `AED ${property.basePrice * bookingNights}`}
                      </span>
                    </div>
                    {bookingSubtotal > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span>Platform fee ({platformFeePercentage}%):</span>
                        <span className="font-medium">
                          {effectiveLinkCode ? formatCurrency(platformFee) : `AED ${platformFee}`}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between items-center border-t pt-2 font-medium">
                      <span>Total:</span>
                      <span>{effectiveLinkCode ? formatCurrency(bookingTotal) : `AED ${bookingTotal}`}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Booking Button and Guest Auth/Booking Form */}
            {!isAdmin && canSelectDates && (
               <> 
                {/* Guest count selection */}
                {(dateRange.from && dateRange.to) && (
                      <div className="mt-4 space-y-2">
                      <label className="text-sm font-medium">Number of Guests</label>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setGuestCount(prev => Math.max(1, prev - 1))}
                          disabled={guestCount <= 1}
                        >
                          -
                        </Button>
                        <span className="w-8 text-center">{guestCount}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setGuestCount(prev => property?.maxGuests ? Math.min(property.maxGuests, prev + 1) : prev + 1)}
                          disabled={property?.maxGuests ? guestCount >= property.maxGuests : false}
                        >
                          +
                        </Button>
                      </div>
                      {property && (
                        <p className="text-xs text-muted-foreground">
                          Maximum {property.maxGuests} guest{property.maxGuests > 1 ? 's' : ''} allowed
                        </p>
                      )}
                      {!guestValidation && (
                        <p className="text-xs text-red-500">
                          Please select between 1 and {property?.maxGuests || 1} guests
                        </p>
                      )}
                      </div>
                      )}
                {!user && showAuthForm ? (
                <div className="mt-4 p-4 border rounded-md bg-muted/30">
                  <h3 className="text-lg font-semibold mb-2">Sign in or Register to Book</h3>
                  <GuestAuthForm 
                    redirectPath={window.location.pathname + window.location.search}
                    onSuccess={() => setShowAuthForm(false)}
                  />
                </div>
                ) : (
                <>
                  {/* Show validation errors */}
                  {dateRange.from && dateRange.to && !stayValidation.isValid && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      {stayValidation.message}
                    </div>
                  )}
                  
                  <Button 
                    className="w-full mt-4"
                    onClick={() => {
                      if (!user) {
                        setShowAuthForm(true);
                      } else {
                        setShowBookingForm(true);
                      }
                    }}
                    disabled={!dateRange.from || !dateRange.to || isAuthLoading || !isBookingValid}
                  >
                    {isAuthLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : !isBookingValid && dateRange.from && dateRange.to ? (
                      "Invalid Booking"
                    ) : (
                      "Book Now"
                    )}
                  </Button>
                </>
                )}

                {(showBookingForm && dateRange.from && dateRange.to && (
                <div className="mt-4 space-y-4">
                    <BookingForm
                      propertyId={property.id}
                      checkInDate={dateRange.from}
                      checkOutDate={dateRange.to}
                      guestCount={guestCount}
                      nightlyPrices={nightlyPrices}
                      totalAmount={bookingTotal}
                      agentId={property.agentId}
                      agentLinkId={property.linkId}
                      onBookingCreated={(bookingId: number) => {
                        window.location.href = `/bookings/${bookingId}/confirmation`;
                      }}
                    />
                </div>
                ))}
            </>
            )}

            {/* Admin Edit Button */}
            {isAdmin  && (
              <div className="mt-4 flex flex-col gap-2">
                <Link href={`/admin/properties/${propertyId}/edit`}>
                  <Button className="w-full">
                    Edit Property
                  </Button>
                </Link>
                {property.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 hover:text-green-800"
                    >
                      <Check className="h-4 w-4 mr-2" /> Approve
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800"
                    >
                      <X className="h-4 w-4 mr-2" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            )}
            
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Property Details */}
        <div className="lg:col-span-2">
            
             {/* Property Features */}
           <div className="mb-8">
              <h3 className="text-lg font-medium mb-4">Features</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-2">
                {(property.features?.length > 20 && !showAllFeatures
                  ? property.features.slice(0, 20)
                  : property.features
                )?.map((feature: string, index: number) => (
                  <div key={index} className="flex items-center">
                    <Check className="h-4 w-4 mr-2 text-green-600" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              {property.features?.length > 20 && (
                <button
                  className="mt-2 text-primary underline text-sm focus:outline-none"
                  onClick={() => setShowAllFeatures((prev) => !prev)}
                >
                  {showAllFeatures ? 'Show less' : `Show all (${property.features.length})`}
                </button>
              )}
            </div>
          {/* Property Description */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">About this property</h2>
            <div 
              className="text-muted-foreground mb-4 prose max-w-none"
              dangerouslySetInnerHTML={{ __html: property.description }}
            />
            
           
            
            {/* Owner Contact Information - only visible to managers and agents */}
            {user && (user.role === 'manager' || user.role === 'agent' || user.role === 'admin') && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <h3 className="text-lg font-medium text-blue-900 mb-3">Owner Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">Name:</span>
                    <span className="font-medium">{property.ownerName || "Not available"}</span>
                  </div>
                  {property.ownerCompany && (
                    <div className="flex items-center justify-between">
                      <span className="text-blue-700">Company:</span>
                      <span className="font-medium">{property.ownerCompany}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">Email:</span>
                    <a href={`mailto:${property.ownerEmail}`} className="text-blue-600 hover:underline font-medium">
                      {property.ownerEmail || "Not available"}
                    </a>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-700">Phone:</span>
                    <span className="font-medium">{property.ownerPhone || "Not available"}</span>
                  </div>
                  
                  {property.ownerPhone && (
                    <a 
                      href={`https://wa.me/${property.ownerPhone.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md flex items-center justify-center mt-3 w-full"
                    >
                      <svg viewBox="0 0 24 24" className="h-5 w-5 mr-2" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"></path>
                      </svg>
                      Contact on WhatsApp
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
      
      {/* Location Details Section */}
      {(property.locationDescription || (property.lat && property.lng)) && (
        <div className="mt-12 border-t pt-8">
          <h2 className="text-2xl font-bold mb-6">Location</h2>
          
          {property.locationDescription && (
            <div className="mb-8">
              <div 
                className="text-muted-foreground prose max-w-none"
                dangerouslySetInnerHTML={{ __html: property.locationDescription }}
              />
            </div>
          )}
          
          {property.lat && property.lng && (
            <div className="mb-8">
              <h3 className="text-lg font-medium mb-4">Property Location</h3>
              <div className="w-full h-[400px] rounded-lg overflow-hidden border">
                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14405.123456789!2d${property.lng}!3d${property.lat}!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM${Math.abs(property.lat).toFixed(6)}%C2%B0${property.lat >= 0 ? 'N' : 'S'}+${Math.abs(property.lng).toFixed(6)}%C2%B0${property.lng >= 0 ? 'E' : 'W'}!5e0!3m2!1sen!2s!4v1234567890`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Property Location"
                />
              </div>
            </div>
          )}
        </div>
      )}
      
      </div>
            )   
  return renderWithLayout(content); 
}